import React, { memo, useCallback } from "react";
import t from "../../../../../../../../../i18n";
import Select from "@ui/Select";
import Switch from "@ui/Switch";
import { useGUIEditorContext } from "../../../../../../hook/context/gui";
import { type DurationT } from "../../../../../../schema/period";
import { formSectionItemFull } from "../_styles/form";
import {
  useBlockOptionUpdateCallback,
  useSyncedBlockOptionUpdateCallback,
} from "../hook/useOptionUpdateCallback";
import { useLocalPlotPeriod, useSharedPlotPeriod } from "../../../Block/Plot/hook/usePlotPeriod";
import { usePlotLink } from "../../../Block/Plot/hook/usePlotLink";
import { DATE_RANGE_NAVIGATION_PRESETS } from "../../../../../../component/navigation";

export const TimeScaleSetting = memo(() => {
  const { block } = useGUIEditorContext();

  const [, setLocalPlotPeriod] = useLocalPlotPeriod({
    scope: block?.id ?? "",
    defaultValue: block?.config.options?.temporalZoom ?? "ALL",
  });
  const [, setSharedPlotPeriod] = useSharedPlotPeriod({
    scope: block?.dashboardId ?? "",
  });
  const { setLink } = usePlotLink(block);

  const updateBlockOption = useBlockOptionUpdateCallback();
  const updateSyncedBlockOption = useSyncedBlockOptionUpdateCallback();

  const updateTemporalZoom = useCallback(
    (value: string) => {
      if (!block) return;
      const v = value === "" ? "ALL" : (value as DurationT);
      if (block.config.options?.sync) {
        updateSyncedBlockOption("temporalZoom", v);
        setSharedPlotPeriod(v);
      } else {
        updateBlockOption("temporalZoom", v);
        setLocalPlotPeriod(v);
      }
    },
    [block, setSharedPlotPeriod, updateSyncedBlockOption, setLocalPlotPeriod, updateBlockOption],
  );

  const updateSync = useCallback(
    (checked: boolean) => {
      updateBlockOption("sync", checked);
      setLink(checked);
    },
    [updateBlockOption, setLink],
  );

  return (
    <>
      <Switch
        checked={block?.config.options?.sync === undefined ? false : block.config.options.sync!}
        onChange={e => updateSync(e.target.checked)}
        className={formSectionItemFull}
        tooltipOverlay={t("tooltip.editor.plot.temporal_sync")}
      >
        {t("project.dashboard.editor.gui.section.display.sync.title")}
      </Switch>
      <Select
        prompt={block?.config.options?.temporalZoom ?? "ALL"}
        label={t("project.dashboard.editor.gui.section.display.temporal_zoom.title")}
        options={DATE_RANGE_NAVIGATION_PRESETS}
        value={block?.config.options?.temporalZoom || "ALL"}
        onChange={e => {
          updateTemporalZoom(e.target.value);
        }}
        className={formSectionItemFull}
        clearable={block?.config.options?.temporalZoom !== "ALL"}
        tooltipOverlay={t("tooltip.editor.plot.encoding_zoom")}
      />
    </>
  );
});
